import React, { useState, useEffect } from "react";
//import { ArrayUtils } from "@nubeteck/utils";
import { Checkbox, DatePicker, Input, Radio, Select } from "antd";
import EditorInput from "src/components/other/html-editor/html-editor";
import CodeEditor from "@uiw/react-textarea-code-editor";
import {
  BOOLEAN,
  CLAVE,
  CODE_EDITOR,
  EDITOR,
  EMAIL,
  FECHA,
  LONG_TEXT,
  MULTI_OPTIONS,
  NUMBER,
  OPTIONS,
  RADIO,
  SHORT_TEXT,
} from "src/constants";
import { ISettingsField } from "src/Interfaces/generalSettings";
import { useLocale } from "src/i18n/localeContext";
import { getSettingsPage } from "src/Api/content";

import dayjs from "dayjs";
import { DATE_DISPLAY_FORMAT } from "src/utils/date-util";

const ConfigurationInput = ({
  data,
  onChange,
  onBlur,
  disabled=false,
  readOnly=false,
  value: formValue,
}: {
  data: ISettingsField;
  onChange: (value: any) => void;
  onBlur?: () => void;
  disabled?: boolean;
  readOnly?: boolean;
  value?: any;
}) => {
  const { locale } = useLocale();
  const [settingsPage, setSettingsPage] = useState<any>(null);

  useEffect(() => {
    getSettingsPage(locale).then((res) => {
      setSettingsPage(res.data);
    }).catch(() => {
      // Error handling intentionally empty
    });
  }, [locale]);

  // 🔹 Normalizar opciones (para evitar problemas de orden o idioma)
  const normalizeOptions = React.useMemo(() => {
    if (!data.opciones || data.opciones.length === 0) return [];
    return data.opciones.map((o: any) => {
      const lbl =
        o.label ??
        o.etiqueta ??
        o.nombre ??
        o.opcion ??
        String(o.titulo ?? o.text ?? o.value ?? o.valor ?? "");
      const val = o.value ?? o.valor ?? o.id ?? o.key ?? o.codigo ?? o.code?? o.option ?? o.opcionId;
      return {
        label: String(lbl),
        value: val != null ? String(val) : "",
      };
    });
  }, [data.opciones]);

  // 🔹 Obtener valor actual según idioma - usar valor del formulario si está disponible
  const rawCurrent = React.useMemo(() => {
    // Si hay un valor del formulario, usarlo (prioridad al valor del formulario)
    if (formValue !== undefined && formValue !== null) {
      return formValue;
    }
    // Si no, usar el valor de data según idioma
    return locale === "en" ? (data.value ?? data.valor) : (data.valor ?? data.value);
  }, [locale, data.value, data.valor, formValue]);

  // 🔹 Normalizar valor actual para coincidir con los Selects / Radios
  const getCurrentValue = React.useMemo(() => {
    if (data.tipo === OPTIONS || data.tipo === RADIO) {
      return rawCurrent != null ? String(rawCurrent) : undefined;
    }
    if (data.tipo === MULTI_OPTIONS) {
      const arr = Array.isArray(rawCurrent)
        ? rawCurrent
        : rawCurrent != null
        ? [rawCurrent]
        : [];
      return arr.map((v) => String(v));
    }
    return rawCurrent;
  }, [rawCurrent, data.tipo]);

  // 🔹 Props comunes
  const inputProps = React.useMemo(
    () => ({
      name: `${data.nombre}`,
      options:
        data.tipo === OPTIONS ||
        data.tipo === MULTI_OPTIONS ||
        data.tipo === RADIO
          ? normalizeOptions
          : [],
    }),
    [data.nombre, data.tipo, normalizeOptions],
  );

  // 🔹 Filtro de búsqueda para selects
  const filterOption = (input: string, option?: { label?: React.ReactNode; value?: string }) =>
    String(option?.label ?? "").toLowerCase().includes(input.toLowerCase());

  // 🔹 Mapeo por tipo de input
  const mapping = React.useMemo<Record<string, React.JSX.Element>>(
    () => ({
      [FECHA]: (
        <DatePicker
          {...inputProps}
          value={rawCurrent ? dayjs(rawCurrent) : undefined}
          format={DATE_DISPLAY_FORMAT}
          onChange={(e) => onChange(e)}
          readOnly={readOnly}
          disabled={disabled}
        />
      ),
      [RADIO]: (
        <Radio.Group
          {...inputProps}
          size="large"
          value={getCurrentValue}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        />
      ),
      [CLAVE]: (
        <Input
          {...inputProps}
          type="password"
          value={getCurrentValue}
          onChange={(e) => onChange(e.target.value)}
          readOnly={readOnly}
          disabled={disabled}
        />
      ),
      [EDITOR]: (
        <EditorInput
          useVariables={data.useVariables}
          variables={data.variables}
          {...inputProps}
          value={getCurrentValue}
          defaultValue={getCurrentValue}
          onChange={(val) => onChange(val)}
          onBlur={onBlur}
          readOnly={readOnly}
          disabled={disabled}
        />
      ),
      [CODE_EDITOR]: (
        <CodeEditor
          value={getCurrentValue}
          language={data.language}
          placeholder={settingsPage?.translate_template?.placeholder_code || "Introduzca el código"}
          onChange={(evn: any) => onChange(evn.target.value)}
          padding={15}
          style={{
            backgroundColor: "#f5f5f5",
            fontFamily:
              "ui-monospace,SFMono-Regular,SF Mono,Consolas,Liberation Mono,Menlo,monospace",
          }}
          disabled={disabled}
        />
      ),
      [MULTI_OPTIONS]: (
        <Select
          {...inputProps}
          value={getCurrentValue}
          showSearch
          filterOption={filterOption}
          mode="multiple"
          placeholder={settingsPage?.translate_template?.placeholder_select || "Selecciona una opción"}
          onChange={(e) => onChange(e)}
          disabled={disabled}
        />
      ),
      [EMAIL]: (
        <Input
          {...inputProps}
          type="email"
          value={getCurrentValue}
          onChange={(e) => onChange(e.target.value)}
          readOnly={readOnly}
          disabled={disabled}
        />
      ),
      [NUMBER]: (
        <Input
          {...inputProps}
          type="number"
          value={getCurrentValue}
          onChange={(e) => onChange(e.target.value)}
          readOnly={readOnly}
          disabled={disabled}
        />
      ),
      [SHORT_TEXT]: (
        <Input
          {...inputProps}
          type="text"
          value={getCurrentValue}
          placeholder={settingsPage?.translate_template?.placeholder_input || "Escribe un texto"}
          onChange={(e) => onChange(e.target.value)}
          readOnly={readOnly}
          disabled={disabled}
        />
      ),
      [LONG_TEXT]: (
        <Input.TextArea
          {...inputProps}
          value={getCurrentValue}
          rows={2}
          placeholder={settingsPage?.translate_template?.placeholder_description || "Escribe una descripción"}
          onChange={(e) => onChange(e.target.value)}
          readOnly={readOnly}
          disabled={disabled}
        />
      ),
      [OPTIONS]: (
        <Select
          style={{ minWidth: 100 }}
          allowClear
          showSearch
          filterOption={filterOption}
          {...inputProps}
          value={getCurrentValue}
          placeholder={settingsPage?.translate_template?.placeholder_select || "Selecciona una opción"}
          onChange={(e) => onChange(e)}
          disabled={disabled}
        />
      ),
      [BOOLEAN]: (
        <Checkbox
          {...inputProps}
          checked={getCurrentValue == "true" || getCurrentValue === true}
          onChange={(e) => onChange(e.target.checked)}
          disabled={disabled}
        >
          {data?.label}
        </Checkbox>
      ),
    }),
    [inputProps, onChange, data, onBlur, getCurrentValue, rawCurrent, disabled, readOnly, settingsPage],
  );

  return mapping[data.tipo] ?? `[NO INPUT]: ${data.descripcion}`;
};

export default ConfigurationInput;